// Gestion du club (bureau), rangée par thèmes depuis l'onglet Club (D-036) :
//   « Saison, équipes et coachs » : la saison, les équipes, leurs coachs, le code FFBB ;
//   « Couleurs et logo » : l'app aux couleurs du club (D-024) ;
//   « Page du club » : la vitrine (D-031, D-035), les mentions légales et données (D-103) ;
//   « Bénévoles » : le modèle.
// L'admin crée les équipes et nomme les coachs par e-mail (D-008, D-016).
import { decorateTeamRates } from './desist';
import { activeSeason, clubMemberList, clubSettings, clubTeams, clubTechnicals, createSeason, createTeam, deleteTeam, invite, removeClubRole, setClubFfbbCode, teamFootprint, teamSetEntry, teamStaffAccounts, updateClubBrand, updateTeam, type Club, type ClubBrand, type Team } from '../lib/api';
import { entryText } from '../domain/entry';
import { appIconPatch } from './app-icon';
import { ogImagePatch } from './og-image';
import { localDate } from '../domain/schedule';
import { BRAND_PRESETS, contrast, isHexColor } from '../domain/brand';
import { CATEGORIES } from '../domain/categories';
import { modOn, refreshContext, siteOn, type Ctx } from './context';
import { $, $$, confirmTwice, html, mount, openSheet, toast } from './dom';
import { applyBrand, clubMark, logoColors, logoFromFile } from './theme';
import { renderPublicSettings } from './public-admin';
import { renderSocialEditor } from './social';
import { renderLegalSettings } from './club-legal';
import { clubPublicSettings, trialRequests, type TrialRequest } from '../lib/api';
import { modulePreview } from '../lib/modules-api';
import { copyText } from './share';
import { settingsPage } from './nav';
import { siteOffer, siteSettings } from './site-app';
import { siteBrief } from '../lib/site-api';

const GENDER_LABEL = { F: 'Filles', M: 'Garçons', X: 'Mixte' } as const;

/** Saison sportive qui contient aujourd'hui : 1er septembre → 30 juin. */
function currentSeasonBounds(now = new Date()) {
  const y = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return { name: `${y}-${y + 1}`, starts: `${y}-09-01`, ends: `${y + 1}-06-30` };
}

const back = html`<a class="back" href="#/club">‹ Le club</a>`;
const adminClub = (ctx: Ctx): Club | undefined => ctx.adminOf.find(c => c.id === ctx.club?.id) ?? ctx.adminOf[0];
const notAdmin = html`<section class="screen"><div class="empty"><p class="empty-title">Réservé au bureau du club.</p></div></section>`;

/** Nom, couleurs et logo (le nom corrigé par le bureau, Idries : « je voudrais que le bureau puisse le corriger au besoin »). */
export function renderClubIdentity(root: HTMLElement, ctx: Ctx, onBrandChange?: () => void): void {
  const club = adminClub(ctx);
  if (!club) { mount(root, notAdmin); return; }
  mount(root, html`<section class="screen">${back}<header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Nom, couleurs et logo</h2></header>${brandBlock(ctx, club.id)}</section>`);
  wireBrand(root, ctx, club.id, onBrandChange);
}

/** Page du club (le module, D-122 ; un seul nom, B13 ; D-139, maquette n2 : une page dans l'app, la tuile ne sort plus de
 *  l'app) : en ligne ou non, « Voir la page ↗ », « Copier le lien », les abonnés, les essais ; sa ⚙ ouvre Réglages · Page
 *  (#/club/page/reglages) : l'adresse et la mise en ligne, l'accroche, les réseaux, l'entrée aux matchs, les mentions
 *  légales. Module éteint : il reste les mentions légales et données, liées depuis l'app et chaque invitation. */
export async function renderClubPage(root: HTMLElement, ctx: Ctx, sub?: string, open?: string): Promise<void> {
  const club = adminClub(ctx);
  if (!club) { mount(root, notAdmin); return; }
  const page = modOn(ctx, 'page');
  if (!page) {
    mount(root, html`<section class="screen">${back}<header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Mentions légales</h2>
      <p class="muted">Ce que dit la page « Mentions légales et données » du club, liée depuis l’app et chaque invitation.</p></header>
      <div id="club-legal"></div></section>`);
    void renderLegalSettings($(root, '#club-legal')!, club.id);   // mentions légales et données (D-103)
    return;
  }
  if (sub === 'reglages') return renderClubPageSettings(root, ctx, club, open);
  const [pub, pv, trials, offer, site] = await Promise.all([
    clubPublicSettings(club.id).catch(() => null), modulePreview(club.id, 'page').catch(() => null),
    trialRequests([club.id]).catch(() => [] as TrialRequest[]),
    siteOn(ctx) ? siteOffer(ctx, club.id).catch(() => html``) : Promise.resolve(html``),   // D-140 : l'offre « Site personnalisable », ou la carte du site
    siteOn(ctx) ? siteBrief(club.id).catch(() => null) : Promise.resolve(null),
  ]);
  // D-142 : hors bêta (tant que le lot B n'est pas livré), la page gratuite reste seule, sans un mot de l'offre.
  const offered = siteOn(ctx);
  const live = !!(pub?.public_enabled && pub.public_slug);
  const link = pub?.public_slug ? publicLink(pub.public_slug) : null;
  const fresh = trials.filter(t => t.status === 'nouveau').length;
  const followers = pv?.followers ?? 0;
  mount(root, html`
    <section class="screen">
      ${back}
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Page du club</h2>
        <p class="muted">La vitrine du club : prochains matchs, résultats, équipes et créneaux, contact et « Viens essayer ». Elle se met à jour toute seule.</p></header>
      ${offered ? html`<section class="block se-free" data-guide="cp-state">
        <div class="row"><p class="eyebrow" style="margin:0">Ta page aujourd’hui</p><span class="chip ${live ? 'chip-ok' : 'chip-mute'}">${!live ? 'Gratuite · hors ligne'
          : site?.status === 'en_ligne' && site.option ? 'Remplacée par le site' : 'Gratuite · en ligne'}</span></div>
        <strong>Page du club</strong>
        <p class="muted" style="margin:0;font-size:14px">Une page générée, à jour toute seule : matchs, résultats, équipes, contact, « Venez essayer ». Elle reste gratuite.</p>`
      : html`<section class="block" data-guide="cp-state">
        <div class="block-head"><h3>${live ? 'En ligne' : 'Hors ligne'}</h3><span class="chip ${live ? 'chip-ok' : 'chip-mute'}">${live ? 'Visible de tous' : 'Personne ne la voit'}</span></div>`}
        ${live && link ? html`
          <p class="pub-link"><a href="${link}" target="_blank" rel="noopener">${shortLink(link)}</a></p>
          <span class="btn-row"><a class="btn btn-primary btn-sm" href="${link}" target="_blank" rel="noopener" data-guide="cp-see">Voir la page ↗</a>
            <button type="button" class="btn btn-ghost btn-sm" id="cp-copy">Copier le lien</button></span>`
        : html`<p class="muted small">Choisis son adresse et mets-la en ligne : elle se remplit toute seule avec les matchs et les équipes.</p>
          <a class="btn btn-primary btn-sm" href="#/club/page/reglages/en-ligne" data-guide="cp-online">Mettre en ligne</a>`}
      </section>
      ${offer}
      <div class="kpi-grid">
        <div class="kpi"><span class="eyebrow">Abonnés</span><b>${followers}</b><small>${followers ? 'suivent une équipe du club' : 'Personne ne suit encore une équipe'}</small></div>
        <div class="kpi"><span class="eyebrow">Essais</span><b>${fresh}</b><small>${fresh ? 'demandes à traiter' : 'Rien à traiter'}</small></div>
      </div>
      <a class="card card-row" href="#/essais" data-guide="cp-trials"><span class="mi-ico" aria-hidden="true"><span class="ti ti-door"></span></span>
        <span class="card-main"><strong>Essais et demandes</strong><span class="muted small">${fresh ? `${fresh} à traiter · « Viens essayer » de la page` : 'Les demandes d’essai venues de la page et des liens d’équipe'}</span></span>
        <span class="occ-go" aria-hidden="true">›</span></a>
    </section>`);
  $(root, '#cp-copy')?.addEventListener('click', () => { if (link) void copyText(link); });
}

/** L'adresse publique : le sous-domaine du club quand le service en a (D-032), sinon /c/<adresse>. */
const publicLink = (slug: string) => {
  const base = (import.meta.env.VITE_CLUB_HOST_BASE as string | undefined)?.trim();
  return base ? `${location.protocol}//${slug}.${base}/` : `${location.origin}/c/${slug}`;
};
const shortLink = (link: string) => link.replace(/^https?:\/\//, '').replace(/\/$/, '');

/** Réglages · Page (D-139) : quatre rubriques repliables ; l'entrée aux matchs y vient d'Équipes et coachs. */
async function renderClubPageSettings(root: HTMLElement, ctx: Ctx, club: Club, open?: string): Promise<void> {
  const season = await activeSeason(club.id).catch(() => null);
  const [pub, teams] = await Promise.all([
    clubPublicSettings(club.id).catch(() => null),
    season ? clubTeams(club.id, season.id).catch(() => [] as Team[]) : Promise.resolve([] as Team[]),
  ]);
  const live = !!(pub?.public_enabled && pub.public_slug);
  const set = teams.filter(t => t.entry_mode).length;
  const again = () => renderClubPageSettings(root, ctx, club);
  const entryBody = (el: HTMLElement) => {
    mount(el, html`
      <p class="muted small nv-intro">Les conditions des matchs à domicile, équipe par équipe : le public les lit sur la page, les membres dans Matchs.</p>
      ${teams.length ? html`<ul class="nv-rows">${teams.map(t => html`<li><button type="button" class="nv-row" data-entry="${t.id}">
        <span class="card-main"><strong>${t.name}</strong><span class="muted small">${entryText({ mode: t.entry_mode ?? null, price_cents: t.entry_price_cents ?? null, free_members: t.entry_free_members ?? true, note: t.entry_note ?? null }) ?? 'À préciser'}</span></span>
        <span class="nv-go" aria-hidden="true">›</span></button></li>`)}</ul>` : html`<p class="hint">Crée d'abord les équipes de la saison (Club › Équipes et coachs).</p>`}`);
    for (const b of $$<HTMLButtonElement>(el, '[data-entry]')) b.addEventListener('click', () => openEntrySheet(teams.find(t => t.id === b.dataset.entry)!, teams, again));
  };
  settingsPage(root, {
    key: 'page', open: open ?? null,
    intro: 'Ce que montre la page publique : son adresse, l’accroche, les réseaux, l’entrée aux matchs, les mentions légales.',
    rubrics: [
      { key: 'en-ligne', icon: 'ti-globe', title: 'En ligne et adresse', guide: 'cp-sec-online',
        sum: live && pub?.public_slug ? `En ligne · ${shortLink(publicLink(pub.public_slug))}` : 'Hors ligne · l’adresse, l’accroche, « Viens essayer »',
        body: (el) => renderPublicSettings(el, club.id, ctx.clubs.find(c => c.id === club.id) ?? club) },
      { key: 'reseaux', icon: 'ti-megaphone', title: 'Réseaux sociaux', sum: 'Les comptes du club, sur la page et dans l’app',
        body: (el) => renderSocialEditor(el, club.id, null, 'Réseaux sociaux du club', true) },
      { key: 'entree', icon: 'ti-ticket', title: 'Entrée aux matchs', sum: teams.length ? `${set} / ${teams.length} équipes réglées` : 'Pas encore d’équipe',
        body: entryBody },
      // D-140 : le site personnalisable, ses réglages renvoient à Site du club et au bureau (D-142 : là où l'offre est proposée).
      ...(siteOn(ctx) ? [{ key: 'site', icon: 'ti-pen', title: 'Site personnalisable', sum: 'Qui le modifie, la boutique, l’historique', guide: 'cp-sec-site',
        body: (el: HTMLElement) => void siteSettings(ctx, club.id).then(h => mount(el, h)) }] : []),
      { key: 'mentions', icon: 'ti-lock', title: 'Mentions légales et données', sum: 'Le responsable des données, le contact', guide: 'cp-sec-legal',
        body: (el) => renderLegalSettings(el, club.id) },
    ],
  }, again);
}

/** Saison, équipes, coachs, code FFBB. */
export async function renderClub(root: HTMLElement, ctx: Ctx, onBrandChange?: () => void): Promise<void> {
  const club: Club | undefined = adminClub(ctx);
  if (!club) {
    mount(root, html`<section class="screen"><div class="empty"><p class="empty-title">Réservé au bureau du club.</p></div></section>`);
    return;
  }
  const season = await activeSeason(club.id);
  if (!season) {
    const b = currentSeasonBounds();
    mount(root, html`
      <section class="screen">
        <header class="screen-head"><p class="eyebrow">Club</p><h2>${club.name}</h2></header>
        <section class="block">
          <h3>Aucune saison ouverte</h3>
          <p class="muted">Les équipes appartiennent à une saison. Ouvre la saison en cours pour commencer.</p>
          <button type="button" class="btn btn-primary" id="b-season">Ouvrir la saison ${b.name}</button>
        </section>
      </section>`);
    $(root, '#b-season')!.addEventListener('click', async () => {
      try { await createSeason(club.id, b.name, b.starts, b.ends); await renderClub(root, ctx, onBrandChange); }
      catch { toast('Création impossible.', 'ko'); }
    });
    return;
  }

  const teams = await clubTeams(club.id, season.id);
  const [staff, settings, members] = await Promise.all([Promise.all(teams.map(t => teamStaffAccounts(t.id))), clubSettings(club.id),
    clubMemberList(club.id).catch(() => [])]);
  // Le coach par son nom (B20) : l'e-mail seulement tant qu'il n'a pas de nom (invitation pas encore acceptée).
  const nameOf = new Map(members.filter(m => m.user_id && m.name && m.name !== m.email).map(m => [m.user_id!, m.name]));

  mount(root, html`
    <section class="screen">
      ${back}
      <header class="screen-head">
        <p class="eyebrow">${club.name} · saison ${season.name}</p>
        <h2>Équipes et coachs</h2>
      </header>
      <section class="block">
        <div class="block-head">
          <h3>Équipes</h3>
          <button type="button" class="btn btn-primary btn-sm" id="b-team" data-guide="club-new-team">+ Équipe</button>
        </div>
        ${teams.length ? html`<ul class="team-list">${teams.map((t, i) => html`
          <li class="team-row">
            <a class="team-main" href="#/t/${t.id}">
              <strong>${t.name}</strong>
              <span class="muted small">${t.category ?? ''}${t.category ? ' · ' : ''}${GENDER_LABEL[t.gender]}</span>
            </a>
            <div class="team-coaches">
              ${staff[i]!.length
                ? staff[i]!.map(s => html`<span class="coach-chip">${nameOf.get(s.user_id) ?? s.email}</span>`)
                : html`<span class="muted small">Aucun coach</span>`}
            </div>
            <div class="team-acts">
              <button type="button" class="btn btn-ghost btn-sm" data-guide="club-invite-coach" data-coach="${t.id}">Inviter un coach</button>
              <button type="button" class="btn btn-ghost btn-sm" data-edit-team="${t.id}">Modifier</button>
              ${modOn(ctx, 'page') ? '' : html`<button type="button" class="btn btn-link btn-sm team-entry" data-entry="${t.id}">${entryText({ mode: t.entry_mode ?? null, price_cents: t.entry_price_cents ?? null, free_members: t.entry_free_members ?? true, note: t.entry_note ?? null }) ?? 'Entrée aux matchs : à préciser'} ›</button>`}
            </div>
          </li>`)}</ul>`
          : html`<p class="muted">Pas encore d'équipe cette saison.</p>`}
      </section>
      <section class="block" id="tech-block"></section>
      <section class="block">
        <h3>FFBB</h3>
        <form class="inline-form" id="f-ffbb" novalidate>
          <input name="code" value="${settings?.ffbb_club_code ?? ''}" placeholder="Code club, ex. OCC0032016" maxlength="10"
                 autocapitalize="characters" aria-label="Code FFBB du club">
          <button class="btn btn-ghost btn-sm" type="submit">Enregistrer</button>
        </form>
        <p class="hint">Avec ce code, chaque coach relie son équipe à ses compétitions : calendrier, salles, scores et classement arrivent tout seuls.</p>
      </section>
    </section>`);

  void renderTechBlock($(root, '#tech-block')!, club.id);
  // D-148 : le taux de « prévenu à temps » de chaque équipe (rien de nominatif).
  void decorateTeamRates(root, club.id).catch(() => undefined);
  const ff = $<HTMLFormElement>(root, '#f-ffbb')!;
  ff.addEventListener('submit', async (e) => {
    e.preventDefault();
    const code = String(new FormData(ff).get('code') ?? '').trim().toUpperCase();
    if (code && !/^[A-Z]{3}[0-9]{7}$/.test(code)) { toast('Format attendu : 3 lettres + 7 chiffres (ex. OCC0032016).', 'ko'); return; }
    try { await setClubFfbbCode(club.id, code || null); toast('Code FFBB enregistré'); }
    catch { toast('Enregistrement impossible.', 'ko'); }
  });

  $(root, '#b-team')!.addEventListener('click', () => {
    openSheet('Nouvelle équipe', html`
      <form class="form" id="f-team" novalidate>
        <label class="field"><span>Nom</span><input name="name" required maxlength="80" placeholder="ex. U15 filles 1"></label>
        <label class="field"><span>Catégorie</span>
          <select name="category">${CATEGORIES.map(c => html`<option>${c}</option>`)}</select>
        </label>
        <div class="field"><span>Joueuses ou joueurs ?</span>
          <div class="seg" role="radiogroup">
            ${(['F', 'M', 'X'] as const).map((g, i) => html`<button type="button" role="radio" data-g="${g}" aria-pressed="${String(i === 0)}">${GENDER_LABEL[g]}</button>`)}
          </div>
        </div>
        <p class="hint">Le genre accorde tous les textes de l'équipe : « joueuse », « présente »…</p>
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Créer l'équipe</button>
      </form>`, (sheet, close) => {
      let gender: 'F' | 'M' | 'X' = 'F';
      for (const b of $$<HTMLButtonElement>(sheet, '[data-g]')) b.addEventListener('click', () => {
        gender = b.dataset.g as typeof gender;
        $$(sheet, '[data-g]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      });
      const f = $<HTMLFormElement>(sheet, '#f-team')!;
      f.addEventListener('submit', async (e) => {
        e.preventDefault();
        const d = new FormData(f);
        const name = String(d.get('name') ?? '').trim();
        if (!name) { $(f, '.form-msg')!.textContent = 'Donne un nom à l\'équipe.'; return; }
        try {
          await createTeam(club.id, season.id, name, String(d.get('category')), gender);
          close(); toast('Équipe créée');
          await refreshContext(ctx);
          await renderClub(root, ctx, onBrandChange);
        } catch { $(f, '.form-msg')!.textContent = 'Création impossible. Réessaie.'; }
      });
    });
  });

  // Modifier ou supprimer une équipe (D-107) : le bureau.
  for (const b of $$<HTMLButtonElement>(root, '[data-edit-team]')) {
    b.addEventListener('click', () => {
      const team = teams.find(t => t.id === b.dataset.editTeam)!;
      openSheet(`Modifier · ${team.name}`, html`
        <form class="form" id="f-edit-team" novalidate>
          <label class="field"><span>Nom</span><input name="name" required maxlength="80" value="${team.name}"></label>
          <label class="field"><span>Catégorie</span>
            <select name="category">${CATEGORIES.map(c => html`<option ${c === team.category ? 'selected' : ''}>${c}</option>`)}</select>
          </label>
          <div class="field"><span>Joueuses ou joueurs ?</span>
            <div class="seg" role="radiogroup">
              ${(['F', 'M', 'X'] as const).map(g => html`<button type="button" role="radio" data-g="${g}" aria-pressed="${String(g === team.gender)}">${GENDER_LABEL[g]}</button>`)}
            </div>
          </div>
          <p class="form-msg" role="alert"></p>
          <button class="btn btn-primary" type="submit">Enregistrer</button>
        </form>
        <section class="sheet-sec team-danger">
          <h4>Supprimer l'équipe</h4>
          <p class="hint" id="del-what">On regarde ce que l'équipe emporte…</p>
          <button type="button" class="btn btn-danger-ghost" id="b-del-team" disabled>Supprimer l'équipe</button>
        </section>`, (sheet, close) => {
        let gender = team.gender;
        for (const g of $$<HTMLButtonElement>(sheet, '[data-g]')) g.addEventListener('click', () => {
          gender = g.dataset.g as typeof gender;
          $$(sheet, '[data-g]').forEach(x => x.setAttribute('aria-pressed', String(x === g)));
        });
        const f = $<HTMLFormElement>(sheet, '#f-edit-team')!;
        f.addEventListener('submit', async (e) => {
          e.preventDefault();
          const d = new FormData(f);
          const name = String(d.get('name') ?? '').trim();
          if (!name) { $(f, '.form-msg')!.textContent = 'Donne un nom à l\'équipe.'; return; }
          try { await updateTeam(team.id, { name, category: String(d.get('category') ?? '') || null, gender }); close(); toast('Équipe modifiée'); await renderClub(root, ctx, onBrandChange); }
          catch { $(f, '.form-msg')!.textContent = 'Enregistrement impossible. Réessaie.'; }
        });
        const del = $<HTMLButtonElement>(sheet, '#b-del-team')!;
        void teamFootprint(team.id).then(fp => {
          const parts = [fp.members ? `${fp.members} ${fp.members > 1 ? 'membres quittent' : 'membre quitte'} l'équipe (leurs fiches restent au club)` : '',
            fp.events ? `${fp.events} ${fp.events > 1 ? 'séances et matchs supprimés' : 'séance ou match supprimé'}, avec leurs présences et leurs stats` : '',
            fp.plays ? `${fp.plays} ${fp.plays > 1 ? 'plays supprimés' : 'play supprimé'}` : ''].filter(Boolean);
          $(sheet, '#del-what')!.textContent = parts.length ? `${parts.join(' ; ')}. C'est définitif.` : 'L\'équipe est vide : rien d\'autre ne part.';
          del.disabled = false;
        }).catch(() => { $(sheet, '#del-what')!.textContent = 'Tout ce qui est lié à l\'équipe part avec elle. C\'est définitif.'; del.disabled = false; });
        confirmTwice(del, 'Supprimer définitivement', async () => {
          try { await deleteTeam(team.id); close(); toast(`« ${team.name} » supprimée`); await renderClub(root, ctx, onBrandChange); }
          catch { toast('Suppression impossible.', 'ko'); }
        });
      });
    });
  }
  // Entrée aux matchs à domicile, par équipe (D-106) ; avec la page du club, elle se règle dans ses réglages (D-139).
  for (const b of $$<HTMLButtonElement>(root, '[data-entry]')) {
    b.addEventListener('click', () => openEntrySheet(teams.find(t => t.id === b.dataset.entry)!, teams, () => void renderClub(root, ctx, onBrandChange)));
  }
  for (const b of $$<HTMLButtonElement>(root, '[data-coach]')) {
    b.addEventListener('click', () => {
      const team = teams.find(t => t.id === b.dataset.coach)!;
      openSheet(`Coach · ${team.name}`, html`
        <form class="form" id="f-coach" novalidate>
          <label class="field"><span>Adresse e-mail du coach</span>
            <input name="email" type="email" inputmode="email" autocomplete="off" required placeholder="prenom@exemple.fr">
          </label>
          <p class="hint">S'il n'a pas encore de compte, il reçoit une invitation par e-mail. Il se connecte ensuite avec un code reçu à cette adresse.</p>
          <p class="form-msg" role="alert"></p>
          <button class="btn btn-primary" type="submit">Inviter</button>
        </form>`, (sheet, close) => {
        const f = $<HTMLFormElement>(sheet, '#f-coach')!;
        f.addEventListener('submit', async (e) => {
          e.preventDefault();
          const r = await invite('coach', team.id, String(new FormData(f).get('email') ?? ''));
          if (r.error) { $(f, '.form-msg')!.textContent = r.error === 'input' ? 'Adresse e-mail invalide.' : 'Invitation impossible. Réessaie.'; return; }
          close();
          toast(r.status === 'invited' ? 'Invitation envoyée' : 'Coach ajouté (compte existant)');
          await renderClub(root, ctx, onBrandChange);
        });
      });
    });
  }
}

// --- Responsable technique (D-068) -------------------------------------------------------
async function renderTechBlock(el: HTMLElement, clubId: string): Promise<void> {
  const list = await clubTechnicals(clubId).catch(() => [] as { user_id: string; name: string; since: string }[]);
  const reload = () => void renderTechBlock(el, clubId);
  mount(el, html`
    <div class="block-head"><h3>Responsable technique</h3><button type="button" class="btn btn-ghost btn-sm" id="b-tech">+ Inviter</button></div>
    <p class="hint">Staff de toutes les équipes côté sportif : planning, séances, plays et exercices de tous les coachs, matchs, compositions, renforts. Pas les licences ni l'argent, les partenaires, la page publique ou les bénévoles.</p>
    ${list.length ? html`<ul class="vg-mini">${list.map(p => html`<li><span class="vg-init" aria-hidden="true">${p.name.charAt(0).toUpperCase()}</span><strong>${p.name}</strong>
      <button type="button" class="link-x" data-untech="${p.user_id}" aria-label="Retirer ${p.name}">retirer</button></li>`)}</ul>` : ''}`);
  for (const b of $$<HTMLButtonElement>(el, '[data-untech]')) b.addEventListener('click', async () => {
    if (!b.dataset.sure) { b.dataset.sure = '1'; b.textContent = 'confirmer'; return; }
    try { await removeClubRole(clubId, b.dataset.untech!, 'technical'); toast('Retiré'); reload(); } catch { toast('Impossible pour le moment.', 'ko'); }
  });
  $(el, '#b-tech')!.addEventListener('click', () => openSheet('Inviter un responsable technique', html`
    <form class="form" id="f-tech" novalidate>
      <label class="field"><span>Prénom</span><input name="name" maxlength="40" autocomplete="off" placeholder="ex. Stéphane"></label>
      <label class="field"><span>Adresse e-mail</span><input name="email" type="email" inputmode="email" autocomplete="off" required placeholder="prenom@exemple.fr"></label>
      <p class="hint">S'il n'a pas encore de compte, il reçoit une invitation ; il se connecte avec un code reçu à cette adresse.</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Inviter</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-tech')!;
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const d = new FormData(f);
      const r = await invite('technical', clubId, String(d.get('email') ?? '').trim(), undefined, String(d.get('name') ?? '').trim() || undefined);
      if (r.error) { $(f, '.form-msg')!.textContent = r.error === 'input' ? 'Adresse e-mail invalide.' : 'Invitation impossible. Réessaie.'; return; }
      close(); toast(r.status === 'invited' ? 'Invitation envoyée' : 'Responsable technique ajouté (compte existant)'); reload();
    });
  }));
}

// --- Couleurs du club (D-024) ------------------------------------------------------
// L'admin choisit une couleur, un sigle et un logo ; toute l'app se recolore en
// direct pendant qu'il essaie, et pour tout le club une fois enregistré.
const PRESETS = BRAND_PRESETS;

function brandBlock(ctx: Ctx, clubId: string) {
  const b = ctx.clubs.find(c => c.id === clubId);
  if (!b) return '';
  const current = isHexColor(b.primary_color) ? b.primary_color.toUpperCase() : '#E8590C';
  const second = isHexColor(b.secondary_color) ? b.secondary_color.toUpperCase() : '';
  return html`
    <section class="block" id="couleurs">
      <div class="block-head"><h3>Identité du club</h3><span class="muted small">vue par tout le club</span></div>
      <label class="field"><span>Nom du club <span class="muted small">· en-tête de l’app, page du club, liens partagés</span></span><input id="c-name" maxlength="120" required value="${b.name}" autocomplete="organization" data-guide="club-name"></label>
      <div class="brand-preview" id="c-preview">${clubMark(b, 'lg')}<span class="tb-text"><span class="tb-name">${b.name}</span><span class="tb-role">Aperçu</span></span></div>
      <div class="field"><span>Couleur principale <span class="muted small">· barre, bandeaux, boutons</span></span>
        <div class="swatches" id="c-sw1" data-guide="club-colors">
          ${PRESETS.map(c => html`<button type="button" class="swatch" data-color="${c}" style="--sw:${c}" aria-label="Couleur ${c}" aria-pressed="${String(c === current)}"></button>`)}
          <label class="swatch swatch-custom" title="Autre couleur"><input type="color" id="c-color" value="${current.toLowerCase()}" aria-label="Autre couleur"></label>
        </div>
      </div>
      <div class="field"><span>Couleur secondaire <span class="muted small">· sur-titres, chiffres, jauges, onglet actif</span></span>
        <div class="swatches" id="c-sw2">
          <button type="button" class="swatch swatch-none" data-color2="" aria-label="Pas de couleur secondaire" aria-pressed="${String(!second)}">∅</button>
          ${PRESETS.map(c => html`<button type="button" class="swatch" data-color2="${c}" style="--sw:${c}" aria-label="Secondaire ${c}" aria-pressed="${String(c === second)}"></button>`)}
          <label class="swatch swatch-custom" title="Autre couleur"><input type="color" id="c-color2" value="${(second || '#F28C28').toLowerCase()}" aria-label="Autre couleur secondaire"></label>
        </div>
      </div>
      <div class="field" id="c-fromlogo" hidden><span>Prises dans ton logo <span class="muted small">· touche : principale, appui long : secondaire</span></span><div class="swatches" id="c-logosw"></div></div>
      <p class="hint" id="c-legible"></p>
      <label class="field"><span>Sigle, affiché quand il n'y a pas de logo</span><input id="c-short" maxlength="6" value="${b.short_name ?? ''}" placeholder="ex. BCR" autocapitalize="characters"></label>
      <div class="field"><span>Logo</span>
        <div class="btn-row">
          <label class="btn btn-ghost btn-sm" data-guide="club-logo">Choisir une image<input type="file" id="c-logo" accept="image/png,image/jpeg,image/webp" hidden></label>
          ${b.logo_url ? html`<button type="button" class="btn btn-link btn-sm" id="c-nologo">Retirer le logo</button>` : ''}
        </div>
        <p class="hint">PNG, JPEG ou WebP, de préférence carré. L'app le réduit avant l'envoi.</p>
      </div>
      <button type="button" class="btn btn-primary" id="c-save" data-guide="club-save" disabled>Enregistrer</button>
    </section>`;
}

function wireBrand(root: HTMLElement, ctx: Ctx, clubId: string, onBrandChange?: () => void) {
  const b = ctx.clubs.find(c => c.id === clubId);
  if (!b) return;
  const draft: ClubBrand = { ...b };
  const save = $<HTMLButtonElement>(root, '#c-save')!;
  const preview = () => {
    applyBrand(draft, false);
    $(root, '#c-preview')!.innerHTML = html`${clubMark(draft, 'lg')}<span class="tb-text"><span class="tb-name">${draft.name}</span><span class="tb-role">Aperçu</span></span>`.s;
    save.disabled = false;
  };
  const legible = () => {
    const p = isHexColor(draft.primary_color) ? draft.primary_color : '#E8590C';
    const ink = contrast('#FFFFFF', p) >= contrast('#16181D', p) ? 'texte blanc' : 'texte foncé';
    $(root, '#c-legible')!.textContent = `✓ Lisible : ${ink} sur la principale. Une couleur trop claire est assombrie pour les textes, éclaircie en mode sombre.`;
  };
  const pick = (c: string) => {
    draft.primary_color = c.toUpperCase();
    $$(root, '.swatch[data-color]').forEach(s => s.setAttribute('aria-pressed', String(s.dataset.color === draft.primary_color)));
    legible(); preview();
  };
  const pick2 = (c: string) => {
    draft.secondary_color = c ? c.toUpperCase() : null;
    $$(root, '.swatch[data-color2]').forEach(s => s.setAttribute('aria-pressed', String((s.dataset.color2 || null) === (draft.secondary_color ?? null))));
    preview();
  };
  const fromLogo = async () => {
    const cols = await logoColors(draft.logo_url);
    const box = $(root, '#c-fromlogo')!; box.hidden = !cols.length;
    $(root, '#c-logosw')!.innerHTML = cols.map(c => `<button type="button" class="swatch" data-logo="${c}" style="--sw:${c}" aria-label="${c}"></button>`).join('');
    $$<HTMLButtonElement>(root, '[data-logo]').forEach(s => {
      let t = 0;
      s.addEventListener('pointerdown', () => { t = window.setTimeout(() => { t = -1; pick2(s.dataset.logo!); }, 500); });
      s.addEventListener('pointerup', () => { if (t > 0) { clearTimeout(t); pick(s.dataset.logo!); } t = 0; });
      s.addEventListener('contextmenu', e => e.preventDefault());
    });
  };
  legible(); void fromLogo();
  $$<HTMLButtonElement>(root, '.swatch[data-color2]').forEach(s => s.addEventListener('click', () => pick2(s.dataset.color2 ?? '')));
  $<HTMLInputElement>(root, '#c-color2')!.addEventListener('input', (e) => pick2((e.target as HTMLInputElement).value));
  $$<HTMLButtonElement>(root, '.swatch[data-color]').forEach(s => s.addEventListener('click', () => pick(s.dataset.color!)));
  $<HTMLInputElement>(root, '#c-color')!.addEventListener('input', (e) => pick((e.target as HTMLInputElement).value));
  $<HTMLInputElement>(root, '#c-name')!.addEventListener('input', (e) => { draft.name = (e.target as HTMLInputElement).value.replace(/\s+/g, ' ').trim(); preview(); });
  $<HTMLInputElement>(root, '#c-short')!.addEventListener('input', (e) => { draft.short_name = (e.target as HTMLInputElement).value.trim() || null; preview(); });
  $<HTMLInputElement>(root, '#c-logo')!.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    try { draft.logo_url = await logoFromFile(file); preview(); void fromLogo(); }
    catch { toast('Image refusée : PNG, JPEG ou WebP.', 'ko'); }
  });
  $(root, '#c-nologo')?.addEventListener('click', () => { draft.logo_url = null; preview(); });
  save.addEventListener('click', async () => {
    if (!draft.name || draft.name.length > 120) { toast('Donne un nom au club.', 'ko'); $<HTMLInputElement>(root, '#c-name')!.focus(); return; }
    save.disabled = true;
    try {
      const icon = await appIconPatch({ ...draft, app_icon_for: ctx.clubs.find(c => c.id === clubId)?.app_icon_for ?? null }).catch(() => null);   // D-102
      const og = await ogImagePatch({ ...draft, name: draft.name ?? ctx.clubs.find(c => c.id === clubId)?.name ?? '', og_image_for: ctx.clubs.find(c => c.id === clubId)?.og_image_for ?? null }).catch(() => null);   // aperçu des liens
      await updateClubBrand(clubId, { name: draft.name, primary_color: draft.primary_color, secondary_color: draft.secondary_color ?? null, logo_url: draft.logo_url, short_name: draft.short_name, ...(icon ?? {}), ...(og ?? {}) });
      await refreshContext(ctx);
      ctx.club = ctx.clubs.find(c => c.id === clubId) ?? ctx.club;
      applyBrand(ctx.club);
      toast('Enregistré : tout le club voit le nom, les couleurs et le logo');
      onBrandChange?.();
    } catch { save.disabled = false; toast('Enregistrement impossible.', 'ko'); }
  });
}

export const seasonEnd = (fallbackFrom: string) => {
  const b = currentSeasonBounds(new Date(fallbackFrom));
  return b.ends < fallbackFrom ? localDate(new Date(new Date(fallbackFrom).getTime() + 90 * 864e5)) : b.ends;
};

/** L'entrée aux matchs à domicile d'une équipe (D-106) : ce que lit le public sur la page du club, les membres dans Matchs. */
export function openEntrySheet(team: Team, teams: Team[], onDone: () => void): void {
  const mode = team.entry_mode ?? '';
  openSheet(`Entrée · ${team.name}`, html`
    <form class="form" id="f-entry" novalidate>
      <p class="hint">Les conditions que le bureau fixe pour les matchs à domicile de l'équipe : le public les lit sur la page du club, les membres dans « Matchs du club ». Rien n'est affiché tant que tu n'as rien choisi.</p>
      <label class="check"><input type="radio" name="mode" value="" ${mode ? '' : 'checked'}><span><strong>Ne rien afficher</strong></span></label>
      <label class="check"><input type="radio" name="mode" value="libre" ${mode === 'libre' ? 'checked' : ''}><span><strong>Entrée libre</strong></span></label>
      <label class="check"><input type="radio" name="mode" value="payante" ${mode === 'payante' ? 'checked' : ''}><span><strong>Entrée payante</strong></span></label>
      <label class="field"><span>Prix (€)</span><input name="price" type="number" inputmode="decimal" min="0.5" max="100" step="0.5" value="${team.entry_price_cents ? team.entry_price_cents / 100 : ''}" placeholder="5"></label>
      <label class="check"><input type="checkbox" name="free" ${team.entry_free_members ?? true ? 'checked' : ''}><span>Gratuite pour les licenciés du club</span></label>
      <label class="field"><span>Tes conditions en plus (facultatif)</span><input name="note" maxlength="80" value="${team.entry_note ?? ''}" placeholder="gratuit pour les moins de 12 ans, buvette ouverte…"></label>
      <label class="check"><input type="checkbox" name="all"><span>Les mêmes conditions pour toutes les équipes</span></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-entry')!;
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const m = String(d.get('mode') ?? '');
      const mode = m === 'libre' || m === 'payante' ? m : null;
      const price = Math.round(Number(String(d.get('price') ?? '').replace(',', '.')) * 100);
      if (mode === 'payante' && !(price >= 50 && price <= 10000)) { $(f, '.form-msg')!.textContent = 'Indique un prix entre 0,50 et 100 €.'; return; }
      try {
        const targets = d.get('all') === 'on' ? teams : [team];
        for (const tm of targets) await teamSetEntry(tm.id, mode, mode === 'payante' ? price : null, d.get('free') === 'on', String(d.get('note') ?? '').trim() || null);
        close();
        toast('Entrée enregistrée');
        onDone();
      } catch { $(f, '.form-msg')!.textContent = 'Enregistrement impossible. Réessaie.'; }
    });
  });
}
